"use client";

import { AnimatePresence, motion, useInView } from "framer-motion";
import { CalendarClock, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { cn, formatNumber } from "@/lib/utils";
import { Avatar } from "@/ui/elements";
import { EASE } from "../../constants";
import { DotBadge, Panel, type Stat, StatStrip } from "../../primitives";
import { SCENE_SLOWDOWN, scaleDelay } from "../../timing";
import type { SceneProps } from "../../types";
import { useSceneFinish } from "../../use-scene-finish";
import { useStickToBottom } from "../../use-stick-to-bottom";
import { CLASS_INFO, FEED, type FeedItem, SEGMENT } from "./data";

const fmtNum = (v: number) => formatNumber(v, { precision: 0 });
const fmtMoney = (v: number) => `$${formatNumber(v, { precision: 0 })}`;

function SeatGrid({ booked, reduce }: { booked: number; reduce: boolean }) {
  const { capacity, startBooked } = CLASS_INFO;
  return (
    <div className="grid max-w-[13.5rem] grid-cols-10 gap-1.5 sm:max-w-none">
      {Array.from({ length: capacity }).map((_, i) => {
        const filled = i < booked;
        const isNew = i >= startBooked && i < booked;
        return (
          <motion.div
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length static grid
            key={i}
            className={cn("rounded-[4px]", filled ? "bg-primary" : "bg-muted")}
            style={{ aspectRatio: "1 / 1" }}
            initial={reduce || !isNew ? false : { scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, ease: EASE }}
          />
        );
      })}
    </div>
  );
}

function ClassPanel({
  booked,
  waitlist,
  reduce,
}: {
  booked: number;
  waitlist: number;
  reduce: boolean;
}) {
  const { capacity } = CLASS_INFO;
  const spotsLeft = capacity - booked;
  const full = spotsLeft <= 0;
  const nearlyFull = !full && spotsLeft <= 3;
  const status = full ? "full" : nearlyFull ? "nearly" : "open";
  return (
    <div className="flex flex-col gap-2">
      {/* The under-booked class Helios is filling */}
      <div className="rounded-md border border-border bg-background p-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/50">
            <CalendarClock className="h-4 w-4 text-foreground" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">
              {CLASS_INFO.day} · {CLASS_INFO.time}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {CLASS_INFO.name} · {CLASS_INFO.location} · {CLASS_INFO.coach}
            </p>
          </div>
        </div>

        <div className="mt-3">
          <SeatGrid booked={booked} reduce={reduce} />
        </div>

        <div className="mt-2.5 flex items-center justify-between">
          <p className="text-[13px] font-medium text-foreground">
            <motion.span key={booked} className="tabular-nums">
              {booked}
            </motion.span>{" "}
            <span className="text-muted-foreground">of {capacity} booked</span>
            {waitlist > 0 ? (
              <span className="text-muted-foreground"> · {waitlist} waitlisted</span>
            ) : null}
          </p>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              key={status}
              initial={reduce ? false : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0, y: -4 }}
              transition={{ duration: 0.25, ease: EASE }}
            >
              <DotBadge dot={full ? "bg-primary" : nearlyFull ? "bg-success" : null}>
                {full ? "Class full" : nearlyFull ? "Nearly full" : `${spotsLeft} spots open`}
              </DotBadge>
            </motion.span>
          </AnimatePresence>
        </div>
      </div>

      {/* The smart segment it targets — desktop enrichment beside the feed */}
      <div className="hidden rounded-md border border-border bg-background p-3 lg:block">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <span className="text-[13px] font-medium text-foreground">Smart segment</span>
          <Badge variant="outline" className="ml-auto bg-background text-[10px]">
            {formatNumber(SEGMENT.count)} members
          </Badge>
        </div>
        <p className="mt-1.5 text-[13px] text-muted-foreground">{SEGMENT.name}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {SEGMENT.chips.map((chip) => (
            <span
              key={chip}
              className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground"
            >
              {chip}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function FirstName() {
  return (
    <span className="mx-0.5 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-white/20 px-1.5 py-0.5 align-middle text-[11px] font-semibold leading-none text-white">
      <span className="opacity-60">{"{ }"}</span> First name
    </span>
  );
}

function MessageBubble({ body, reduce }: { body: string; reduce: boolean }) {
  const [before, after] = body.split("{first}");
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.45, ease: EASE }}
      className="flex justify-end"
    >
      <div className="max-w-[16rem] rounded-2xl rounded-br-md bg-outgoing-bubble px-3 py-2 text-[13px] leading-snug text-white shadow-sm">
        {before}
        <FirstName />
        {after}
      </div>
    </motion.div>
  );
}

function SystemLine({ text, reduce }: { text: string; reduce: boolean }) {
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: EASE }}
      className="flex items-center gap-2 py-0.5"
    >
      <span className="h-px flex-1 bg-border" />
      <span className="whitespace-nowrap text-[11px] text-muted-foreground">{text}</span>
      <span className="h-px flex-1 bg-border" />
    </motion.div>
  );
}

function BookingRow({
  name,
  src,
  note,
  waitlist,
  waitlistPos,
  reduce,
}: {
  name: string;
  src?: string;
  note?: string;
  waitlist?: boolean;
  waitlistPos?: number;
  reduce: boolean;
}) {
  const subtitle = waitlist
    ? "Added to waitlist · just now"
    : note
      ? `${note} · just now`
      : "Booked · just now";
  return (
    <motion.div
      layout
      initial={reduce ? false : { opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, ease: EASE }}
      className="flex items-center gap-2.5 rounded-lg border border-border bg-background p-2 shadow-sm"
    >
      <Avatar name={name} src={src} size="sm" subtle={!src} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-foreground">{name}</p>
        <p className="truncate text-[11px] text-muted-foreground">{subtitle}</p>
      </div>
      {waitlist ? (
        <DotBadge dot="bg-amber-500" className="shrink-0">
          Waitlist{waitlistPos ? ` · #${waitlistPos}` : ""}
        </DotBadge>
      ) : (
        <DotBadge dot="bg-success" className="shrink-0">
          Booked
        </DotBadge>
      )}
    </motion.div>
  );
}

function Feed({ items, reduce }: { items: FeedItem[]; reduce: boolean }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep the latest booking in view without scrolling the page (scene-2 rule).
  // Backs off if the user scrolls up, resumes when they return to the bottom.
  useStickToBottom(scrollRef, items.length, reduce);

  return (
    <Panel className="min-h-0 flex-1 overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border px-3.5 py-2.5">
        <span className="flex shrink-0 items-center gap-1.5 text-[13px] font-medium text-foreground">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" /> Flow active
        </span>
        <span className="min-w-0 truncate text-[13px] text-muted-foreground">
          · Filling {CLASS_INFO.day} {CLASS_INFO.time} {CLASS_INFO.name}
        </span>
      </div>
      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-3.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <AnimatePresence initial={false}>
          {items.map((item) => {
            if (item.kind === "message")
              return <MessageBubble key={item.id} body={item.body} reduce={reduce} />;
            if (item.kind === "system")
              return <SystemLine key={item.id} text={item.text} reduce={reduce} />;
            const waitlistPos = item.waitlist
              ? items
                  .slice(0, items.indexOf(item) + 1)
                  .filter((i) => i.kind === "booking" && i.waitlist).length
              : undefined;
            return (
              <BookingRow
                key={item.id}
                name={item.name}
                src={item.src}
                note={item.note}
                waitlist={item.waitlist}
                waitlistPos={waitlistPos}
                reduce={reduce}
              />
            );
          })}
        </AnimatePresence>
      </div>
    </Panel>
  );
}

export function FillClasses({ reduce, paused = false, onComplete }: SceneProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { amount: 0.3 });
  const [visible, setVisible] = useState(0);

  const complete = visible >= FEED.length;

  useSceneFinish({
    active: complete && inView && !paused,
    delay: scaleDelay(2200, SCENE_SLOWDOWN),
    enabled: !reduce,
    onComplete,
  });

  // Reveal one feed item at a time while in view; pause off-screen, when
  // explicitly paused, and freeze once done.
  useEffect(() => {
    if (reduce || paused || !inView || complete) return;
    const base = visible === 0 ? 500 : (FEED[visible - 1]?.delay ?? 1200);
    const id = setTimeout(() => setVisible((v) => v + 1), scaleDelay(base, SCENE_SLOWDOWN));
    return () => clearTimeout(id);
  }, [inView, visible, reduce, paused, complete]);

  const effVisible = reduce ? FEED.length : visible;
  const shown = FEED.slice(0, effVisible);

  // Running booked count = latest revealed item that carries one.
  let booked: number = CLASS_INFO.startBooked;
  for (const item of shown) {
    if ("booked" in item && typeof item.booked === "number") booked = item.booked;
  }
  const waitlist = shown.filter((item) => item.kind === "booking" && item.waitlist).length;
  const filled = booked - CLASS_INFO.startBooked;
  const replies = Math.round(filled * 2.5);
  const recovered = filled * 26;

  const stats: Stat[] = [
    { name: "Sent", value: SEGMENT.count, formatter: fmtNum },
    { name: "Replies", value: replies, formatter: fmtNum },
    { name: "Booked", value: filled, formatter: (v) => `+${fmtNum(v)}` },
    { name: "Recovered", value: recovered, formatter: fmtMoney },
  ];

  return (
    <div ref={rootRef} className="flex h-[36rem] flex-col p-4 sm:p-5 lg:h-full">
      {/* Top level: the playbook + animated rollup stats */}
      <div className="flex flex-col gap-4 border-b border-border pb-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">Fill soft classes</h3>
          <p className="text-sm text-muted-foreground">
            Message the right audience at the right time.
          </p>
        </div>
        <StatStrip stats={stats} />
      </div>

      {/* One instance: the class, its segment, and the live fill. On mobile the
          class + seat grid sit above the live booking feed. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 pt-4 lg:grid lg:grid-cols-[300px_1fr] lg:gap-5">
        <div className="flex shrink-0 flex-col lg:min-h-0 lg:overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <ClassPanel booked={booked} waitlist={waitlist} reduce={reduce} />
        </div>
        <Feed items={shown} reduce={reduce} />
      </div>
    </div>
  );
}
